import BaseObject from '../../base/base_object';
import {GRANT_TYPES} from './oauth_const';
import Event from '../../utils/event';
import {SYSTEM_EVENTS} from '../../events/events';

import Device from '../../utils/device/device';
import { TimeSpan } from "@sparkware/app-utils";
import _ from '../../utils/underscore';
import LoginTimeAnalytics from './states/login_time_analytics';
import ServiceFactory from '../service_factory';
import {isRefreshTokenError} from "../../exceptions/cgp_error_codes";
import {TokenType} from "../../../../common/const/token_type";
import Store from "../../utils/store";
import MIGRATION from "../../../../common/const/migration_consts";
import {UndefinedString} from "../../../../common/types/nullable-types";
import Token from './tokens/token';

interface IRefreshTokenOptions {
    timeSinceLastActivity?: number;
    reason: string;
}

export default class OAuth2BackendProxy extends BaseObject {
    static CLASS = 'OAuth2BackendProxy';

    _tokenUrl = null;
    _logoutUrl = null;
    _serviceFactory: ServiceFactory = null;
    _inactivityTimeoutSettingsPersister = null;

    init(params) {
        super.init(params);

        this._tokenUrl = params.tokenUrl;
        this._logoutUrl = params.logoutUrl;
        this._serviceFactory = params.serviceFactory;
        this._inactivityTimeoutSettingsPersister = params.inactivityTimeoutSettingsPersister;
    }

    /**
     *
     * @param token {string}
     * @param sessionTimerOffset {TimeSpan}
     * @param sourceProductPackage
     * @param brandId
     * @param sourceSubBrandId
     * @param targetSubBrandId
     * @param tokenType
     * @returns {Promise}
     */
    autoLoginWithToken(token, sessionTimerOffset, sourceProductPackage, brandId, sourceSubBrandId, targetSubBrandId, tokenType: TokenType, businessCorrelationId: UndefinedString) {
        if (sessionTimerOffset) {
            sessionTimerOffset = sessionTimerOffset.toString(); // convert from TimeSpan to string
        }
        return LoginTimeAnalytics.forAutologinWithToken().measure(() => {
            let autoLogingParams: any = {
                token: this._fixPlusSignEncoding(token)
            };

            if (!_.isNullOrUndefined(sessionTimerOffset)) {
                autoLogingParams.sessionTimerOffset = sessionTimerOffset;
            }

            if (!_.isNullOrUndefined(sourceProductPackage)) {
                autoLogingParams.sourceProductPackage = sourceProductPackage;
            }
            if (!_.isNullOrUndefined(brandId)) {
                autoLogingParams.brandId = brandId;
            }

            if (!_.isNullOrUndefined(sourceSubBrandId)) {
                autoLogingParams.sourceSubBrandId = sourceSubBrandId;
            }

            if (!_.isNullOrUndefined(targetSubBrandId)) {
                autoLogingParams.targetSubBrandId = targetSubBrandId;
            }

            if(!_.isNullOrUndefined(businessCorrelationId)) {
                autoLogingParams.businessCorrelationId = businessCorrelationId;
            }

            autoLogingParams.tokenType = !_.isNullOrUndefined(tokenType) ? tokenType : TokenType.AuthenticationToken;
            return this._autologin(autoLogingParams);
        });
    }

    _fixPlusSignEncoding(token) {
        // if the token originally had a "+" sign inside of it and the navigation is done through GNWP then shared navigation replaces + sign with a space
        // ideally the one who generated the token should URL encode it but if it doesn't do that our autologin will fail.
        if ('string' === typeof(token)) {
            return token.replace(/ /g, '+');
        } else {
            return token;
        }
    }

    async autoLoginWithTouchId(touchIdToken) {
        let touchParams = _.safeJSON(touchIdToken, null);
        
        if (touchParams && touchParams.touch_token) {
            return LoginTimeAnalytics.forAutologinWithTouchId().measure(() => {
                return this._autologin({
                    token: touchParams.touch_token, //used to be touchtoken
                    deviceid: touchParams.DeviceId,
                    bundleid: touchParams.BundleId,
                    tokenType: TokenType.TouchID
                });
            });
        }

        return Promise.resolve();
    }

    async _autologin(autoLoginParams) {
        autoLoginParams.grant_type = GRANT_TYPES.AUTOLOGIN;

        autoLoginParams.deviceUUID = await this._serviceFactory.webLoginService().getDeviceUUID();     

        return this._login(autoLoginParams);
    }

    loginWithPassword(credentials) {
        credentials.grant_type = GRANT_TYPES.PASSWORD;

        return LoginTimeAnalytics.forLoginWithPassword().measure(() => {
            return this._login(credentials);
        });
    }

    async _login(credentials) {
        try {
            // credentials parameter must come before  this._buildLoginParams()
            // otherwise some parameters like brandId that can be undefined in the credentials to override those returned by this._buildLoginParams()

            let loginParams = Object.assign({}, credentials, this._buildLoginParams(), this._buildCustomSessionTimeTokenRequestParam());

            loginParams.playerMigrated = this._serviceFactory.playerMigrationProxyService().isPlayerMigrated;

            loginParams.businessCorrelationId = this._serviceFactory.playerMigrationProxyService().businessCorrelationId;
            
            if (!_.isNullOrUndefined(credentials.brandId)) {
                loginParams.brandId = credentials.brandId;
            }

            if (!_.isNullOrUndefined(credentials.targetSubBrandId)) {
                loginParams.subBrandId = credentials.targetSubBrandId;
            }

            if (Device.hybrid.isHybrid) {
                await this._addPlayerAppDataToLoginParams(loginParams);
            }

            //Add some Marketing Data to the request REQUIREMENT 1897606
            await this._addMarketingDataToLoginParams(loginParams);

            try {
                let result = await this._serviceFactory.http2Service().post(this._tokenUrl, loginParams);
                if (this._serviceFactory.playerMigrationProxyService().isMigrationEnabledFromConfig) {
                    this._setMigrationSuccessfulCookie();
                }
                const decodedAccessToken = Token.decodeAccessToken(result.access_token);
                if (loginParams.brandId !== decodedAccessToken.brandId) {
                    this._setUkScvMigrationSuccessfulCookie();
                }
                return result;
            } catch (error) {
                Event.fire(SYSTEM_EVENTS.FAIL_LOGIN_RESPONSE);

                this.reThrowError('170',
                    'token API failed',
                    error,
                    {
                        loginParams: Object.assign({}, loginParams, {authenticationdata: '*'}) //we are masking the password so we don't print it in the log
                    });

            }

        }catch (error) {
            this.logger.error('_login Error ', error);
            this._sendAnalytics('login Error', 'login with password ', error?.toString());

            this.reThrowError('183',
                '_login  failed',
                error,
                {});
        }
    }

    private _setMigrationSuccessfulCookie() {
        Store.setCookie(MIGRATION.PLAYER_MIGRATED_COOKIE, "true");
    }

    private _setUkScvMigrationSuccessfulCookie() {
        Store.setCookie(MIGRATION.UK_SCV_PLAYER_MIGRATED_COOKIE, "true");
    }

    logout() {
        return this._serviceFactory.http2Service().post(this._logoutUrl);
    }

    refreshToken(token, options: IRefreshTokenOptions) {

        options = {
            timeSinceLastActivity: 0,
            ...options
        }


        let refreshTokenParams = Object.assign(
            {
                grant_type: GRANT_TYPES.REFRESH_TOKEN,
                refresh_token: token,
                time_since_last_user_activity: TimeSpan.fromMilliseconds(options.timeSinceLastActivity).toString()
            },
            this._buildLoginParams(),
            this._buildCustomSessionTimeTokenRequestParam());

        const executeRefreshToken = () => {
            return this._serviceFactory.http2Service().post(this._tokenUrl, refreshTokenParams);
        }

        return executeRefreshToken()
            .then((data) => {
                this._sendAnalytics('refresh_token_success', 'attempt_1', options.reason);
                return data;
            })
            .catch(err1 => {
                if(isRefreshTokenError(err1.error_code)) {
                    throw err1;
                }
                this.logger.error('Refresh token attempt 1 failed', err1);
                this._sendAnalytics('refresh_token_failure', 'attempt_1', options.reason);
                return new Promise((resolve, reject) => {
                    setTimeout(() => {
                        executeRefreshToken().then(data => {
                            resolve(data);
                            this._sendAnalytics('refresh_token_success', 'attempt_2', options.reason);
                        }).catch(err2 => {
                            this.logger.error('Refresh token attempt 2 failed', err2);
                            this._sendAnalytics('refresh_token_failure', 'attempt_2', options.reason);
                            reject(err2);
                        });
                    }, 2000);
                });
            });
    }

    _buildCustomSessionTimeTokenRequestParam() {
        let customSessionTimeout = this._inactivityTimeoutSettingsPersister.tryGetCustomAccessTokenLifeTime();
        if(!customSessionTimeout) {
            return {};
        } else {
            return {
                custom_session_timeout: customSessionTimeout.toString()
            };
        }
    }

    private async _addPlayerAppDataToLoginParams(loginParams): Promise<void> {
        try {

            return new Promise((resolve, reject) => {
                const appMareketingData = this._serviceFactory.hybridService().getAppMareketingData();
                loginParams.playerAppDetails = JSON.stringify(appMareketingData);
                resolve();
            });
        }
        catch (err) {
            this.logger.error('_addPlaterAppDataToLoginParams failed', err);
        }
    }

    private async _addMarketingDataToLoginParams(loginParams: any): Promise<void> {
        try {
            loginParams.gaSessionID = this._serviceFactory.analyticsService().getGoogleSessionID();
            let marketingParams = this._serviceFactory.webLoginService().getWebLoginMarketingParams();
            const marketingData = await this._serviceFactory.integrationService().getMarketingData(marketingParams);
            if(marketingData?.TestData) {
                loginParams.testData = JSON.stringify(marketingData.TestData);
            }


        } catch (error) {
            this.logger.error('_addMarketingData failed', error);

        }

    }

    _buildLoginParams() {
        const skinService = this._serviceFactory.skinService();
        const integrationService = this._serviceFactory.integrationService();
        const loginParams: any = {
            client_id: skinService.getClientID(),
            client_version: skinService.getVersion(),
            productPackage   : skinService.getProductPackageID(),
            brandId          : skinService.getBrandID(),
            subBrandId       : skinService.getSubBrandID(),
            integration      : integrationService.getRole(),
            isHybrid         : Device.hybrid.isHybrid,
            isHybridEmbedded : Device.hybrid.isEmbedded,
            devicePixelRatio : Device.viewport.pixelRatio,
            screenHeight     : Device.viewport.screenHeight,
            screenWidth      : Device.viewport.screenWidth,
            benchmark        : Device.getBenchMark(),
            langISO2         : this._serviceFactory.languageService().getISO2(),
            country          : this._serviceFactory.countryService().getCountry(),
        };

        const anonymousPlayerId = this._serviceFactory.abTestingService().getAnonymousPlayerId();
        if (anonymousPlayerId) {
            loginParams.anonymousPlayerId = anonymousPlayerId;
        }

        return loginParams;
    }

    _sendAnalytics(category: string, action: string, label?: string) {
        this._serviceFactory.analyticsService().sendOrbitUserFlowEvent({
            category:category,
            action:action,
            label:label
        })
    }
}
